import React from 'react';
import { downloadReport, type GeneratedReport } from '../../api/reportsApi';

/** Renders a generated report (SRS #35, #62–#68) with download actions. */

const sevColor: Record<string, string> = { high: 'var(--ds-danger)', medium: 'var(--ds-warning)', low: 'var(--ds-success)' };

const ReportViewer: React.FC<{ report: GeneratedReport; onClose: () => void; formatLabel?: string }> = ({ report, onClose, formatLabel }) => {
  const c = report.content;
  const text = 'var(--color-text, var(--ds-text))';
  const muted = 'var(--color-text-muted, var(--ds-text-muted))';
  const btn: React.CSSProperties = { padding: '6px 12px', borderRadius: 8, border: '1px solid var(--ds-border, var(--ds-border))', background: 'var(--ds-surface, #fff)', cursor: 'pointer', fontSize: 13, color: text };
  return (
    <div role="dialog" aria-modal="true" aria-label={c.title} style={{ position: 'fixed', inset: 0, background: 'rgba(16,24,40,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: 'var(--ds-surface, #fff)', borderRadius: 14, width: 'min(920px, 100%)', maxHeight: '92vh', overflow: 'auto', padding: '22px 26px', color: text }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: muted }}>{formatLabel || report.format} · confidence {c.confidence}</div>
            <h2 style={{ margin: '4px 0 0', fontSize: 20 }}>{c.title}</h2>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button style={btn} onClick={() => downloadReport(report.id, 'pdf').catch(() => {})}>Download PDF</button>
            <button style={btn} onClick={() => downloadReport(report.id, 'html').catch(() => {})}>HTML</button>
            <button style={btn} onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>
        <p style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.5 }}>{c.summary}</p>
        {c.sections.map((s) => (
          <section key={s.heading} style={{ marginTop: 14 }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 14.5, color: '#1F2A44' }}>{s.heading}</h3>
            {s.body ? s.body.split('\n\n').map((p, i) => <p key={i} style={{ margin: '0 0 8px', fontSize: 14, lineHeight: 1.6 }}>{p}</p>) : <p style={{ color: muted, fontStyle: 'italic', fontSize: 13 }}>Nothing in the conversation covered this.</p>}
            {s.bullets.length > 0 && <ul style={{ margin: '4px 0 0', paddingLeft: 20, fontSize: 14, lineHeight: 1.6 }}>{s.bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>}
          </section>
        ))}
        {c.gaps.length > 0 && (
          <section style={{ marginTop: 14 }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 14.5, color: '#1F2A44' }}>Gaps</h3>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.6 }}>
              {c.gaps.map((g, i) => <li key={i}><span style={{ color: sevColor[g.severity], fontWeight: 600, textTransform: 'uppercase', fontSize: 11 }}>{g.severity}</span> <strong>{g.area}</strong> — {g.description}</li>)}
            </ul>
          </section>
        )}
        {c.recommendations.length > 0 && (
          <section style={{ marginTop: 14 }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 14.5, color: '#1F2A44' }}>Recommendations</h3>
            <ol style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.6 }}>
              {c.recommendations.map((r, i) => <li key={i}><strong>{r.action}</strong>{r.owner ? ` — ${r.owner}` : ''}{r.rationale ? <div style={{ color: muted, fontSize: 13 }}>{r.rationale}</div> : null}</li>)}
            </ol>
          </section>
        )}
        {c.evidence.length > 0 && (
          <section style={{ marginTop: 14 }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 14.5, color: '#1F2A44' }}>Evidence</h3>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.6 }}>
              {c.evidence.map((v, i) => <li key={i}>{v.claim} — <a href={v.source} target="_blank" rel="noreferrer">{v.source}</a></li>)}
            </ul>
          </section>
        )}
        {c.caveats.length > 0 && (
          <section style={{ marginTop: 14 }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 14.5, color: '#1F2A44' }}>Caveats</h3>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: muted, lineHeight: 1.6 }}>{c.caveats.map((x, i) => <li key={i}>{x}</li>)}</ul>
          </section>
        )}
      </div>
    </div>
  );
};

export default ReportViewer;
